// Social screens use v2 exclusively. The adapter keeps the response shapes the screens read;
// authorization and visibility remain the backend's decisions.
import { requestV2 } from './v2-only.ts'
import type { GalleryFilters } from '../gallery-search.ts'

type Sides = typeof import('./social-v2.ts')

const request = (use: (loaded: Sides) => Promise<Response>) =>
  requestV2(() => import('./social-v2.ts'), use)

/** Explore's filters and the last item already shown. */
export type ExploreAsk = {
  last_doc_id: string | null
  limit: number
  safe: boolean | null
  model: string
  media_type: string
  sort_by: 'top' | 'trending' | 'newest'
  orientation: string
  date_range?: string
}

/** A page `{ images, has_more }` for Explore. */
export const exploreFeed = (token: string, ask: ExploreAsk) =>
  request((loaded) => loaded.exploreFeed(token, {}, ask))

/** The search and landing pages' feed tabs. */
export type LegacyFeed = 'top' | 'newest' | 'video'

/** Each feed tab keeps its own last item. */
export type LegacyAsk = {
  last_doc_id_newest: string | null
  last_doc_id_video: string | null
  last_doc_id: string | null
  safe: boolean | null
  model: string
}

export const legacyFeed = (token: string, feed: LegacyFeed, ask: LegacyAsk) =>
  request((loaded) => loaded.legacyFeed(token, {}, feed, ask))

type Picture = { id: string } & Record<string, unknown>

export const checkLiked = (token: string, imageId: string) =>
  request((loaded) => loaded.checkLiked(token, {}, imageId))

export const setLiked = (token: string, imageData: Picture, liked: boolean) =>
  request((loaded) => loaded.setLiked(token, {}, imageData.id, liked))

export const viewImage = (token: string, imageData: Picture) =>
  request((loaded) => loaded.viewImage(token, {}, imageData.id))

/** A profile's public posts, likes or the owner's private gallery. */
export type ProfileAsk = {
  target_uid: string
  feed_type: 'posts' | 'likes' | 'private'
  limit: number
  last_doc_id: string | null
  folder: string | null
  sort: 'newest' | 'oldest' | 'most_liked' | 'most_viewed'
  /** One's own posts or private gallery only: the screen's search and filters, sent as `galleryQuery` makes them. */
  filters?: Partial<GalleryFilters>
}

export const profileFeed = (
  token: string,
  ask: ProfileAsk,
  signal?: AbortSignal,
) =>
  request((loaded) => loaded.profileFeed(token, signal ? { signal } : {}, ask))

/**
 * The choices one made across one's own posts or private gallery, to offer as their filters: v2's
 * `{ total, first_made_on, last_made_on, kinds, models, poses, qualities, styles, expressions, folders }`. `fresh`
 * reads past the browser's minute-old copy (after keeping, filing or publishing).
 */
export const mediaFacets = (
  token: string,
  list: 'posts' | 'private',
  options: { signal?: AbortSignal; fresh?: boolean } = {},
) =>
  request((loaded) =>
    loaded.mediaFacets(
      token,
      options.signal ? { signal: options.signal } : {},
      list,
      options.fresh === true,
    ),
  )

export const profileInfo = (token: string, targetUid: string) =>
  request((loaded) => loaded.profileInfo(token, {}, targetUid))

export const setFollowing = (token: string, uid: string, follow: boolean) =>
  request((loaded) => loaded.setFollowing(token, {}, uid, follow))

export type FollowListAsk = {
  target_uid: string
  list_type: 'followers' | 'following'
  offset: number
  limit: number
}

export const followList = (token: string, ask: FollowListAsk) =>
  request((loaded) => loaded.followList(token, {}, ask))

export const followingFeed = (token: string) =>
  request((loaded) => loaded.followingFeed(token, {}))

/** Public reads stay signed out; the backend alone decides whether an item is visible. */
export const readPicture = (
  token: string | null,
  imageId: string,
  _userUid?: string,
) => request((loaded) => loaded.readPicture(token, {}, imageId))

export const siteCount = (token: string) =>
  request((loaded) => loaded.siteCount(token, {}))
